import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import VirtualList from "../../components/ui/VirtualList";
import { formatCount, rankEntries } from "./orgChartModel";

// Searchable job picker for company-sized job lists. Replaces a native
// <select> that would mount one <option> per job: the menu renders only the
// rows in view and is portalled to <body>, so a modal's scroll area never
// clips it.
const ROW_H = 46;
const MENU_MAX_H = 300;
const MENU_GAP = 6;

const optionText = (opt) => (opt ? `${opt.label}${opt.code ? ` (${opt.code})` : ""}` : "");

export default function JobCombobox({
  id,
  value,
  options,
  onChange,
  placeholder = "Search by job name or code…",
  noneLabel,
  excludeValue,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const [scrollTarget, setScrollTarget] = useState(null);
  const [menuBox, setMenuBox] = useState(null);

  const selectedValue = value == null ? "" : String(value);
  const selected = useMemo(
    () => (selectedValue ? options.find((o) => o.value === selectedValue) || null : null),
    [options, selectedValue],
  );

  const results = useMemo(() => {
    if (!open) return [];
    const excluded = excludeValue != null ? String(excludeValue) : null;
    const base = excluded ? options.filter((o) => o.value !== excluded) : options;
    if (query.trim()) return rankEntries(base, query);
    return noneLabel ? [{ value: "", label: noneLabel, none: true }, ...base] : base;
  }, [open, options, query, excludeValue, noneLabel]);

  const place = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 16;
    const above = r.top - 16;
    const dropUp = below < 220 && above > below;
    const maxHeight = Math.max(140, Math.min(MENU_MAX_H, dropUp ? above : below));
    setMenuBox({
      left: r.left,
      width: r.width,
      top: dropUp ? undefined : r.bottom + MENU_GAP,
      bottom: dropUp ? window.innerHeight - r.top + MENU_GAP : undefined,
      maxHeight,
    });
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setActive(-1);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled || open) return;
    place();
    setOpen(true);
    setQuery("");
    setActive(-1);
  }, [disabled, open, place]);

  // Start the list at the current selection.
  useEffect(() => {
    if (!open || query) return;
    const i = results.findIndex((o) => o.value === selectedValue);
    setScrollTarget(i >= 0 ? i : null);
    // only when the menu opens or the search is cleared
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      place();
    };
    const onDown = (e) => {
      if (inputRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close();
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, place, close]);

  const choose = (opt) => {
    onChange(opt.value);
    close();
  };

  const moveActive = (next) => {
    setActive(next);
    setScrollTarget(next);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) openMenu();
      else moveActive(Math.min(results.length - 1, active + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (open) moveActive(Math.max(0, active - 1));
    } else if (e.key === "Enter") {
      if (open && results[active]) {
        e.preventDefault();
        choose(results[active]);
      }
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab" && open) {
      close();
    }
  };

  const showClear = Boolean(noneLabel && selected && !disabled);

  return (
    <div className={`joh-combobox${open ? " open" : ""}${showClear ? " has-clear" : ""}`}>
      <input
        id={id}
        ref={inputRef}
        type="text"
        className="joh-combobox-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        disabled={disabled}
        placeholder={open && selected ? optionText(selected) : placeholder}
        value={open ? query : optionText(selected)}
        title={optionText(selected) || undefined}
        onFocus={openMenu}
        onClick={openMenu}
        onChange={(e) => {
          if (!open) openMenu();
          setQuery(e.target.value);
          setActive(e.target.value.trim() ? 0 : -1);
        }}
        onKeyDown={onKeyDown}
        onBlur={close}
      />
      {showClear && (
        <button
          type="button"
          className="joh-combobox-clear"
          title="Clear selection"
          aria-label="Clear selection"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange("")}
        >
          ✕
        </button>
      )}

      {open && menuBox && createPortal(
        <div
          ref={menuRef}
          className="joh-combobox-menu"
          style={{
            left: menuBox.left,
            width: menuBox.width,
            top: menuBox.top,
            bottom: menuBox.bottom,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {results.length === 0 ? (
            <div className="joh-combobox-empty">No matching jobs</div>
          ) : (
            <VirtualList
              id={listId}
              role="listbox"
              items={results}
              itemHeight={ROW_H}
              maxHeight={menuBox.maxHeight - 30}
              padding={6}
              resetKey={query}
              scrollToIndex={scrollTarget}
              renderItem={(opt, i, style) => (
                <div
                  key={opt.value || "__none"}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={opt.value === selectedValue}
                  className={`joh-combobox-option${i === active ? " active" : ""}${opt.value === selectedValue ? " selected" : ""}${opt.none ? " none" : ""}`}
                  style={style}
                  onMouseMove={() => { if (i !== active) setActive(i); }}
                  onClick={() => choose(opt)}
                >
                  <span className="joh-combobox-option-line">
                    <span className="joh-combobox-option-label">{opt.label}</span>
                    {opt.code && <span className="joh-combobox-option-code">{opt.code}</span>}
                  </span>
                  {opt.sub && <span className="joh-combobox-option-sub">{opt.sub}</span>}
                </div>
              )}
            />
          )}
          <div className="joh-combobox-footer">
            {query.trim() ?
              `${formatCount(results.length)} match${results.length === 1 ? "" : "es"}`
            : `${formatCount(results.length - (noneLabel ? 1 : 0))} jobs · type to filter`}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
